import * as adminApi from '../admin/adminApi.ts';
import type { AdminRun } from '../admin/adminApi.ts';
import { artifactHref, costCell, ownerCell, runHref } from '../admin/derive.ts';
import { AdminPage, LoadState, useLoaded } from '../components/AdminPage.tsx';

/** An ISO instant to the minute, in UTC, as the mono face shows it. Anything else is shown as is. */
export function formatStarted(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(iso) ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}Z` : iso;
}

/**
 * `#/admin/runs` (docs/specs/accounts.md, section 8): every run, whoever owns it. A row opens the
 * EXISTING run view and artifact view — an admin may read any run (section 3), so the routes are
 * the ones the owner uses, and nothing is forked. The server does not page this list yet.
 */
export function AdminRunsView(): JSX.Element {
  const { state, inFlight, reload } = useLoaded(adminApi.listRuns);
  const runs = state.status === 'ready' ? state.data : [];
  const meta =
    state.status === 'ready'
      ? [
          `${runs.length} runs`,
          `${runs.filter((r) => r.over_cost).length} over cost`,
          `${runs.filter((r) => r.report_credit === 'refund_due').length} refund due`,
        ]
      : [];

  return (
    <AdminPage title="Runs" meta={meta}>
      <section className="admin-section">
        <div className="tick">
          <span className="label">§ Every run, newest first</span>
        </div>
        {state.status !== 'ready' ? (
          <LoadState state={state} inFlight={inFlight} onRetry={reload} />
        ) : (
          <>
            <RunsTable runs={runs} />
            {/* The server scans every run file for this list, so one read at a time: the hook
                ignores a reload while a request is in flight, and the button says so. */}
            <button className="btn admin-refresh" type="button" disabled={inFlight} onClick={reload}>
              {inFlight ? 'Reading…' : 'Read again'}
            </button>
          </>
        )}
      </section>
    </AdminPage>
  );
}

/** The table, given the runs: apart from the loading above so that a test renders it from data. */
export function RunsTable({ runs }: { runs: AdminRun[] }): JSX.Element {
  if (runs.length === 0) return <p className="muted">No run has been started yet.</p>;
  return (
    <div className="panel scroll-x">
      <table className="data-table">
        <caption className="sr-only">Runs</caption>
        <thead>
          <tr>
            {['run', 'owner', 'started', 'status', 'cost', 'marks', 'artifact'].map((heading) => (
              <th key={heading} scope="col" className={heading === 'cost' ? 'num' : undefined}>
                <span className="label">{heading}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id}>
              <th scope="row" className="mono">
                <a href={runHref(run)}>{run.id}</a>
              </th>
              <td className={run.owner_email ? 'mono' : 'muted'}>{ownerCell(run)}</td>
              <td className="mono">
                <time dateTime={run.started_at}>{formatStarted(run.started_at)}</time>
              </td>
              <td>
                <span className={`key status-${run.status}`}>{run.status}</span>
              </td>
              <td className="mono num">{costCell(run)}</td>
              <td>
                {/* Words, not colour alone: both marks are brass, and each says what it is. */}
                <span className="admin-marks">
                  {run.over_cost && <span className="key k-partial">over cost</span>}
                  {run.report_credit === 'refund_due' && <span className="key k-partial">refund due</span>}
                </span>
              </td>
              <td>
                {run.has_artifact ? (
                  <a href={artifactHref(run)} aria-label={`Artifact of run ${run.id}`}>
                    artifact
                  </a>
                ) : (
                  <span className="muted">none yet</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
